import React from "react";

// Article 1 — Marge invisible
const ArticleMargeInvisible = () => (
  <>
    <p>L'autre jour, un artisan boulanger me disait quelque chose qui m'a marqué.</p>

    <blockquote>
      <p>« Christophe, c'est devenu insupportable…</p>
      <p>Le beurre a explosé. L'électricité aussi. Les matières premières augmentent sans arrêt.</p>
      <p>On bosse comme des dingues, on ne voit déjà pas beaucoup nos enfants… et bientôt, on ne pourra même plus les emmener en vacances. »</p>
    </blockquote>

    <p>Et le pire dans tout ça ?</p>

    <p>C'est que cette personne ne ménageait pas ses efforts.</p>

    <ul>
      <li>Réveil en pleine nuit et insomnies.</li>
      <li>Week-ends sacrifiés.</li>
      <li>Pression permanente.</li>
      <li>Clients difficiles.</li>
      <li>Équipe à gérer.</li>
      <li>Factures qui gonflent.</li>
    </ul>

    <p>Comme beaucoup d'artisans boulangers et pâtissiers aujourd'hui, il faisait du chiffre… mais sans retrouver de vraie marge ni de sérénité.</p>

    <p>Et malheureusement, ce cas est loin d'être isolé.</p>

    <p>Depuis plusieurs années, de nombreux professionnels de la boulangerie/pâtisserie ont le sentiment de travailler toujours plus pour gagner toujours moins.</p>

    <p><strong>Pourquoi ?</strong></p>

    <p>Parce qu'en réalité, le problème ne vient pas uniquement du prix du beurre, de la farine ou de l'énergie.</p>

    <p>Le vrai problème, c'est souvent que <strong>la marge est devenue invisible.</strong></p>

    <p>Elle fuit partout :</p>

    <div className="article-side-figure">
      <ul>
        <li>dans les invendus,</li>
        <li>dans certains produits peu rentables,</li>
        <li>dans les achats,</li>
        <li>dans les erreurs de prix,</li>
        <li>dans l'organisation,</li>
        <li>dans le temps de production,</li>
        <li>et parfois simplement dans des habitudes prises depuis des années.</li>
      </ul>
      <figure>
        <img
          src="https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/fmxrpcur_ChatGPT%20Image%2028%20mai%202026%2C%2020_35_35.png"
          alt="Artisan boulanger concentré dans son fournil, devant son four et ses pâtons"
          loading="lazy"
        />
      </figure>
    </div>

    <p>Le plus difficile, c'est que beaucoup d'artisans ne s'en rendent compte qu'au moment où la trésorerie commence à devenir tendue.</p>

    <p>Et à ce stade-là, le stress prend toute la place.</p>

    <p>Dans cet article, je vais vous montrer pourquoi autant de boulangeries travaillent énormément sans retrouver leur rentabilité… et surtout quels sont les premiers leviers concrets à analyser avant que la situation ne devienne critique.</p>

    <h2>Le piège qui se referme doucement</h2>

    <p>Et c'est souvent là que le piège se referme.</p>

    <p>Parce qu'au début, l'artisan tient encore le choc.</p>

    <p><strong>Il compense.</strong></p>

    <ul>
      <li>Il travaille plus vite.</li>
      <li>Il dort un peu moins.</li>
      <li>Il rallonge les journées.</li>
      <li>Il serre les dents quand les factures tombent.</li>
    </ul>

    <p>Et comme la boutique tourne encore, il se dit que ça va finir par passer.</p>

    <p>Mais petit à petit, quelque chose change.</p>

    <p>Le chiffre est toujours là, le panier moyen aussi… et pourtant, il reste moins d'argent à la fin du mois.</p>

    <h2>Le vrai problème est plus discret</h2>

    <p>Alors on cherche des explications immédiates :</p>

    <ul>
      <li>le beurre,</li>
      <li>l'électricité,</li>
      <li>les charges salariales,</li>
      <li>les fournisseurs qui ne jouent pas toujours la transparence,</li>
      <li>la concurrence.</li>
    </ul>

    <p>Bien sûr que tout ça joue.</p>

    <p>Mais très souvent, le vrai problème est plus discret.</p>

    <p>Il s'installe lentement dans l'entreprise, presque sans bruit.</p>

    <ul>
      <li>Quelques dizaines de viennoiseries qui partent à la poubelle chaque soir.</li>
      <li>Des produits très longs à fabriquer mais peu rentables.</li>
      <li>Des références gardées « parce qu'on les a toujours faites ».</li>
      <li>Des prix ajustés au ressenti.</li>
      <li>Une gamme devenue trop large.</li>
      <li>Des produits faits juste pour quelques clients, mais qui prennent du temps.</li>
      <li>Des journées entières passées à produire… sans savoir précisément ce qui rapporte réellement.</li>
    </ul>

    <h2>Quand l'artisan ne voit plus le problème arriver</h2>

    <p>Et le plus dur dans tout ça, c'est qu'un artisan passionné ne voit pas toujours le problème arriver.</p>

    <p>Parce qu'il est dans le fournil.</p>

    <ul>
      <li>Dans l'urgence.</li>
      <li>Dans la production.</li>
      <li>Dans les commandes.</li>
      <li>Dans les employés en retard, ou absents.</li>
      <li>Dans les imprévus.</li>
    </ul>

    <p>Il a la tête partout… <strong>sauf sur les chiffres</strong>.</p>

    <p>Alors il continue à travailler énormément. Parfois même davantage qu'avant.</p>

    <p>Mais la rentabilité, elle, continue de s'échapper.</p>

    <p>Je vois souvent des artisans qui connaissent parfaitement leur métier… mais qui n'ont plus une vision claire de ce qui gagne réellement de l'argent dans leur propre entreprise.</p>

    <p>Et à partir de là, chaque nouvelle hausse devient un coup de massue supplémentaire.</p>

    <h2>Quand l'entreprise commence à être subie</h2>

    <p>Le problème, ce n'est pas seulement la fatigue.</p>

    <p>C'est le moment où <strong>l'entreprise commence à être subie</strong>.</p>

    <ul>
      <li>Quand regarder le compte bancaire devient stressant.</li>
      <li>Quand certaines factures commencent à être repoussées.</li>
      <li>Quand les vacances deviennent impossibles.</li>
      <li>Quand on travaille tout le temps… sans réussir à respirer vraiment, comme avant.</li>
    </ul>

    <h2>Rendre les pertes visibles avant de sauver la marge</h2>

    <p>C'est généralement à ce moment-là qu'il faut arrêter de chercher à produire plus.</p>

    <p>Et commencer enfin à regarder <strong>où l'argent disparaît réellement</strong>.</p>

    <p>Parce qu'avant de sauver la marge… il faut déjà rendre les pertes visibles.</p>

    <p>Et très souvent, les premiers leviers ne sont pas compliqués.</p>

    <p>Ils demandent surtout :</p>

    <ul>
      <li>du recul,</li>
      <li>une analyse honnête,</li>
      <li>et une vision terrain adaptée à la réalité du métier.</li>
    </ul>

    <h2>Mon approche : du concret, du terrain</h2>

    <p>C'est exactement là que je vous propose mon aide — pour ne pas vous rajouter du travail. Vous n'avez plus de temps disponible.</p>

    <p>Mon rôle, c'est de vous aider à :</p>

    <p>Reprendre une vision claire de la situation, identifier les principales fuites de marge et repartir avec des actions concrètes, applicables rapidement dans une vraie boulangerie/pâtisserie.</p>

    <ul>
      <li>Pas des théories.</li>
      <li>Pas des tableaux incompréhensibles.</li>
      <li>Du concret.</li>
      <li>Du terrain. Là d'où je viens.</li>
      <li>Et des décisions basées sur les vrais chiffres.</li>
    </ul>
  </>
);

// Article 2 — Invendus / employé fantôme
const ArticleEmployeFantome = () => (
  <>
    <h2>Cet employé non déclaré</h2>

    <p>Il n'apparaît sur aucune fiche de paie. Les clients ne l'ont jamais vu.</p>

    <p>On le cache bien, pas question que ça se sache…</p>

    <p>Il ne pointe pas le matin, mais il est présent tous les jours, aussi régulier qu'un pain moulé. Il ne prend pas de pause, ne demande pas d'augmentation. Le pire, c'est que les autres membres du personnel ne l'aiment pas beaucoup.</p>

    <p>Et pourtant il est là. Chaque soir. Il apparaît quand la boutique a tiré ses rideaux, quand on débarrasse les rayons, quand on range derrière le comptoir et qu'on fait les comptes de la journée.</p>

    <p><strong>Le bac des invendus.</strong></p>

    <p>Discret. Silencieux. Et terriblement coûteux.</p>

    <h2>Le calcul que personne ne fait</h2>

    <p>La plupart des boulangers regardent leurs invendus comme une fatalité. « Qu'est-ce que tu veux y faire ? » « Ce n'est pas grand-chose. »</p>

    <p>Faisons le calcul de ce « pas grand-chose » ensemble.</p>

    <p>Selon l'ADEME et la Confédération de la boulangerie, environ <strong>10,6 %</strong> de la production de pain dans les boulangeries artisanales est écartée de la vente chaque année <em>(chiffres Bakerstaff)</em>.</p>

    <p>Sur un chiffre d'affaires de 350 000 €, un taux d'invendus de 10 % représente 35 000 € de produits fabriqués mais non vendus, soit environ 25 000 € de marge brute perdue après déduction du coût matières <em>(chiffres Maé Innovation)</em>.</p>

    <p><strong>25 000 €. Par an. Partis à la poubelle.</strong></p>

    <p>C'est plus de 2 000 € par mois. Chaque mois. Douze mois sur douze.</p>

    <p>Ton employé fantôme coûte cher.</p>

    <h2>Ce que tu paies vraiment quand tu jettes</h2>

    <p>Quand tu mets une baguette à la corbeille le soir, tu ne jettes pas juste de la farine et de l'eau.</p>

    <div className="article-side-figure">
      <ul>
        <li>Tu jettes la farine.</li>
        <li>Tu jettes l'énergie du four.</li>
        <li>Tu jettes le temps de ton boulanger.</li>
        <li>Tu jettes la marge que ce produit aurait dû générer.</li>
      </ul>
      <figure>
        <img
          src="https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/5pigynsz_invendus%20excell.png"
          alt="Ordinateur affichant un tableau Excel d'analyse de marge — 10 % d'invendus représentent 25 000 € par an"
          loading="lazy"
        />
      </figure>
    </div>

    <p>10,6 %, ce chiffre paraît abstrait. Ramené à une journée, c'est entre <strong>20 et 80 €</strong> qui partent à la poubelle, selon la taille de ta boulangerie <em>(chiffres MAPA)</em>.</p>

    <p>Multiplie par 300 jours ouvrés.</p>

    <h2>Le piège du « taux acceptable »</h2>

    <p>Dans le secteur, on entend souvent qu'un taux d'invendus de 8 à 10 % est « normal ». Une bonne moyenne se situe en réalité à <strong>3 à 5 %</strong> en nombre de produits ou en chiffre d'affaires <em>(La Toque)</em>.</p>

    <p>La différence entre 10 % et 5 % sur une boulangerie moyenne, c'est plus de <strong>12 000 € de marge brute récupérée par an</strong>.</p>

    <p>Sans travailler plus. Sans embaucher. Sans investir.</p>

    <p>Juste en pilotant mieux ta production.</p>

    <h2>Pourquoi ça arrive ?</h2>

    <p>Ce n'est pas une question de compétence. C'est une question d'<strong>outil</strong>.</p>

    <p>La grande majorité des artisans pilotent leur production à l'instinct et à l'habitude. « Lundi je fais toujours autant de baguettes. » « Le vendredi ça part bien. »</p>

    <p>Sauf que l'instinct ne voit pas les micro-variations. Il ne voit pas que le dernier mercredi pluvieux a fait chuter tes ventes de 15 %. Il ne voit pas que tes croissants partent deux fois mieux en été qu'en hiver.</p>

    <p>Une réduction de seulement 3 % du taux d'invendus peut représenter une augmentation de plusieurs milliers d'euros de bénéfice net sur une année <em>(DGsys)</em>.</p>

    <p>3 %. Pas 50 %. Pas une révolution. Juste 3 % de production en moins sur les bons produits, les bons jours.</p>

    <h2>Ce que ça change concrètement</h2>

    <p>Réduire tes invendus de moitié ne demande pas un logiciel complexe ni une formation de trois jours.</p>

    <p>Ça demande trois choses simples :</p>

    <ul>
      <li>Mesurer ce que tu jettes vraiment, par produit et par jour de la semaine.</li>
      <li>Identifier les 2 ou 3 références qui concentrent le plus de pertes.</li>
      <li>Ajuster la production sur ces références uniquement.</li>
    </ul>

    <p>C'est tout.</p>

    <p>Certaines boulangeries ont constaté une réduction de leurs invendus de <strong>20 % en moins de 3 semaines</strong> grâce à cette seule mesure <em>(Maé Innovation)</em>.</p>

    <p>Pas en 6 mois. En 3 semaines.</p>

    <p>Mais on peut aller encore beaucoup plus loin que ça, et des tas de solutions existent. Des mesures concrètes qui valorisent les invendus, redonnent le moral à l'équipe, et donnent une excellente image auprès de la clientèle.</p>

    <h2>Et toi, combien coûte ton employé fantôme ?</h2>

    <p>Tu n'as peut-être jamais fait ce calcul. C'est normal, personne ne te l'a demandé.</p>

    <p>Prends 5 minutes ce soir :</p>

    <ul>
      <li>Estime ce que tu jettes en moyenne par jour.</li>
      <li>Multiplie par 0,70 pour obtenir la marge perdue.</li>
      <li>Multiplie par 300.</li>
    </ul>

    <p>Le chiffre que tu obtiens, c'est ce que tu pourrais récupérer.</p>

    <p>Si tu veux qu'on le calcule ensemble et qu'on identifie tes premiers leviers, c'est exactement ce que je fais lors d'un premier échange — sans engagement.</p>
  </>
);

export const blogPosts = [
  {
    slug: "invendus-boulangerie-employe-fantome",
    title: "Invendus en boulangerie : l'employé fantôme qui coûte cher chaque mois",
    excerpt:
      "Il n'apparaît sur aucune fiche de paie. Personne ne l'a jamais vu. Et pourtant, chaque soir, il coûte cher à votre boulangerie. Plongez dans l'histoire du bac à invendus, cet employé fantôme qui grignote la marge sans bruit.",
    metaTitle:
      "Invendus en boulangerie : réduisez vos pertes de fin de journée",
    metaDescription:
      "Chaque soir, les invendus en boulangerie grignotent votre marge sans bruit. Découvrez comment ces pertes s'accumulent et comment mieux les maîtriser.",
    date: "2026-03-15",
    dateISO: "2026-03-15",
    dateFormatted: "15 mars 2026",
    readTime: "7 min",
    image:
      "https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/le2bd8p8_fin%20de%20journ%C3%A9e.png",
    imageAlt:
      "Boulangerie en fin de journée — étagères qui se vident et bac à invendus discret au comptoir",
    author: "Christophe Marrama",
    Content: ArticleEmployeFantome,
    cta: {
      title: "Envie de stopper l'hémorragie des invendus ?",
      intro:
        "Je vous ai préparé une page entièrement dédiée à ce sujet : méthode, leviers concrets, exemples chiffrés. Tout y est expliqué simplement, pour que vous puissiez agir dès cette semaine.",
      label: "Découvrir Stop aux invendus",
      to: "/stop-aux-invendus",
    },
  },
  {
    slug: "pourquoi-les-boulangers-travaillent-beaucoup-pour-si-peu-de-marge",
    title: "Pourquoi les boulangers travaillent beaucoup pour si peu de marge ?",
    excerpt:
      "Beurre, électricité, matières premières qui augmentent… Et pourtant, le vrai problème n'est pas toujours là où on le pense. Voici pourquoi la marge devient invisible en boulangerie/pâtisserie.",
    metaTitle:
      "Pourquoi les boulangers travaillent beaucoup pour si peu de marge ? | Croissance Consulting",
    metaDescription:
      "Beaucoup d'artisans boulangers travaillent énormément sans retrouver de marge. Les vraies causes (invendus, achats, organisation) et les premiers leviers à analyser.",
    date: "2026-02-28",
    dateISO: "2026-02-28",
    dateFormatted: "28 février 2026",
    readTime: "6 min",
    image:
      "https://customer-assets.emergentagent.com/job_diagnostic-fournil/artifacts/lbb6c1n2_ChatGPT%20Image%2028%20mai%202026%2C%2019_45_19.png",
    imageAlt:
      "Artisan boulanger pensif devant son ordinateur, ses livres et ses chiffres, atmosphère sereine et professionnelle",
    author: "Christophe Marrama",
    Content: ArticleMargeInvisible,
    cta: {
      title: "Rendre la marge visible — chez vous, sur le terrain",
      intro:
        "Si vous voulez voir clairement où la marge fuit dans votre boulangerie ou pâtisserie, j'ai conçu une approche dédiée : le Diagnostic Immersion Terrain. Observation sur place, analyse rapide, leviers concrets sous 48h.",
      label: "Découvrir le Diagnostic immersion",
      to: "/audit-rentabilite-boulangerie",
    },
  },
];

export const getPostBySlug = (slug) =>
  blogPosts.find((post) => post.slug === slug);
